package co.maytes.standupbot.dashboard.styles

import co.maytes.standupbot.shared.design.StatusHue
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.init.InitSilkContext
import org.jetbrains.compose.web.css.CSSMediaQuery
import org.jetbrains.compose.web.css.StyleScope

private val compact = CSSMediaQuery.Raw("(max-width: ${Layout.breakpointMd}px)")

private val cardRadius = "calc(${Radius.xl} * 1.5)"
private val rowRadius = "calc(${Radius.xl} * 1.25)"
private val roundNumeral = "2.5rem"
private val railDot = "0.625rem"
private val railInset = "2rem"

private fun InitSilkContext.rule(selector: String, build: StyleScope.() -> Unit) {
    stylesheet.registerStyle(selector) { base { Modifier.styleModifier(build) } }
}

private fun InitSilkContext.mediaRule(selector: String, query: CSSMediaQuery, build: StyleScope.() -> Unit) {
    stylesheet.registerStyle(selector) { cssRule(query, null) { Modifier.styleModifier(build) } }
}

private fun StyleScope.flatCard() {
    property("background-color", "${Palette.surfaceSubtle}")
    property("border-radius", cardRadius)
}

private fun StyleScope.column(gap: Any) {
    property("display", "flex")
    property("flex-direction", "column")
    property("gap", "$gap")
}

private fun StyleScope.tabular() {
    property("font-variant-numeric", "tabular-nums")
}

internal fun InitSilkContext.registerMarketingPages() {
    registerPageIntro()
    registerNav()
    registerPlans()
    registerCalculator()
    registerFold()
    registerBandSurfaces()
    registerTimeline()
    registerDocsAndContact()
    registerSignIn()
    registerLegal()
    registerPageReducedMotion()
}

private fun InitSilkContext.registerPageIntro() {
    rule(".mk-intro") {
        property("position", "relative")
        property("padding-top", "calc(${Marketing.HERO_TOP_PAD} * 1.25 + ${HomeTokens.NAV_CLEARANCE} * 0.5)")
        property("padding-bottom", Marketing.INTRO_BOTTOM_PAD)
    }
    rule(".mk-intro-inner") {
        property("position", "relative")
        column(Spacing.lg)
        property("align-items", "center")
        property("text-align", "center")
    }
    rule(".mk-h1") {
        property("margin", "0")
        property("font-weight", "${FontWeight.semibold}")
        property("font-size", Marketing.FONT_H1)
        property("line-height", "1.04")
        property("letter-spacing", "-0.04em")
        property("max-width", HomeTokens.HERO_MEASURE)
        property("text-wrap", "balance")
        property("background-image", "linear-gradient(90deg, ${Palette.textPrimary} 35%, ${Palette.textSecondary})")
        property("-webkit-background-clip", "text")
        property("background-clip", "text")
        property("-webkit-text-fill-color", "transparent")
        property("color", "transparent")
    }
    rule(".mk-actions--center") {
        property("justify-content", "center")
        property("padding-bottom", Marketing.SECTION_PAD)
    }
    rule(".mk-main") {
        property("width", "100%")
        property("flex", "1 0 auto")
    }
    rule(".mk-skip") {
        property("position", "absolute")
        property("left", "${Spacing.sm}")
        property("top", "${Spacing.sm}")
        property("z-index", "${ZIndex.nav + 1}")
        property("transform", "translateY(-200%)")
        property("padding", "${Spacing.xs} ${Spacing.md}")
        property("border-radius", "${Radius.pill}")
        property("background-color", "${Palette.textPrimary}")
        property("color", "${Palette.surface}")
        property("transition", Motion.transitionWith(Marketing.DUR_REVEAL, Motion.EASE_EMPHASIS, "transform"))
    }
    rule(".mk-skip:focus-visible") {
        property("transform", "translateY(0)")
    }
    rule(".mk-intro .mk-lede") {
        property("text-align", "center")
    }
    rule(".mk-section--tight") {
        property("padding-top", "0")
        property("padding-bottom", Marketing.SECTION_PAD_TIGHT)
    }
    rule(".mk-wrap.mk-narrow") {
        property("max-width", Marketing.NARROW_MEASURE)
    }
    rule(".mk-btn--block.mk-btn--block") {
        property("width", "100%")
    }
}

private fun InitSilkContext.registerNav() {
    rule(".mk-site .sb-nav") {
        property("border-bottom", "0")
    }
    rule(".mk-navwrap") {
        property("height", "0")
        property("padding", "0 ${Marketing.GUTTER}")
        property("pointer-events", "none")
    }
    rule(".mk-navwrap .mk-pill.sb-nav") {
        property("pointer-events", "auto")
        property("box-sizing", "border-box")
        property("width", "100%")
        property("max-width", "64rem")
        property("margin", "${Spacing.sm} auto 0")
        property("border", "1px solid ${Palette.border}")
        property("border-radius", "${Radius.pill}")
        property("backdrop-filter", "saturate(180%) blur(14px)")
        property("-webkit-backdrop-filter", "saturate(180%) blur(14px)")
    }
    rule(".mk-pill .mk-wrap") {
        property("max-width", "none")
        property("padding-left", "${Spacing.md}")
        property("padding-right", "${Spacing.sm}")
    }
    rule(".mk-nav-brand-name") {
        property("font-size", "${Typography.h3.size}")
        property("font-weight", "${FontWeight.semibold}")
        property("letter-spacing", "-0.03em")
    }
    rule(".mk-nav-link.mk-nav-link, .mk-nav-link.mk-nav-link:visited") {
        padding()
        property("font-size", "${Typography.body.size}")
        property("font-weight", "${FontWeight.medium}")
        property("color", "${Palette.textSecondary}")
        property("text-decoration", "none")
        property("transition", Motion.transition(Motion.DUR_BASE, "color"))
    }
    mediaRule(".mk-nav-link.mk-nav-link:hover", HoverFinePointerMedia) {
        property("color", "${Palette.textPrimary}")
    }
    rule(".mk-nav-link.mk-nav-link--active, .mk-nav-link.mk-nav-link--active:visited") {
        property("color", "${Palette.textPrimary}")
    }
    rule(".mk-nav-link--small.mk-nav-link--small") {
        property("font-size", "${Typography.bodySmall.size}")
    }
    rule(".mk-nav-menu-btn") {
        property("cursor", "pointer")
        property("list-style", "none")
        property("display", "inline-flex")
        property("align-items", "center")
        property("justify-content", "center")
        property("width", "2.25rem")
        property("height", "2.25rem")
        property("border-radius", "${Radius.pill}")
        property("background-color", "${Palette.surfaceSubtle}")
        property("color", "${Palette.textPrimary}")
        property("font-size", "${Typography.h3.size}")
    }
    rule(".mk-nav-menu-btn:focus-visible") {
        property("outline", FocusRing.OUTLINE)
        property("outline-offset", FocusRing.OFFSET)
    }
    rule(".mk-nav-panel") {
        column(Spacing.xxs)
        property("position", "absolute")
        property("top", "calc(100% + ${Spacing.xs})")
        property("right", "0")
        property("min-width", "12rem")
        property("padding", "${Spacing.xs}")
        property("border-radius", rowRadius)
        property("background-color", "${Palette.surfaceSubtle}")
        property("z-index", "${ZIndex.dropdown}")
    }
}

private fun StyleScope.padding() {
    property("padding", "${Spacing.xs} ${Spacing.sm}")
}

private fun InitSilkContext.registerPlans() {
    rule(".mk-plans") {
        property("display", "grid")
        property("grid-template-columns", "repeat(3, minmax(0, 1fr))")
        property("gap", "${Spacing.md}")
        property("align-items", "stretch")
    }
    mediaRule(".mk-plans", compact) {
        property("grid-template-columns", "minmax(0, 1fr)")
        property("row-gap", "${Spacing.lg}")
    }
    rule(".mk-plan") {
        property("position", "relative")
        column(Spacing.md)
        property("min-width", "0")
        property("padding", "${Spacing.xl} ${Spacing.lg}")
        flatCard()
    }
    rule(".mk-plan--pro") {
        property("background-color", "${Palette.surfaceHover}")
    }
    rule(".mk-plan-flag") {
        property("position", "absolute")
        property("top", "0")
        property("left", "50%")
        property("translate", "-50% -50%")
        property("padding", "${Spacing.xxs} ${Spacing.md}")
        property("border-radius", "${Radius.pill}")
        property("font-size", "${Typography.caption.size}")
        property("font-weight", "${FontWeight.semibold}")
        property("white-space", "nowrap")
        property("color", "${Palette.onPrimary}")
        property("background-color", "${Palette.primary}")
    }
    rule(".mk-plan-name") {
        property("margin", "0")
        property("font-size", "${Typography.h2.size}")
        property("font-weight", "${FontWeight.semibold}")
        property("letter-spacing", "-0.03em")
        property("color", "${Palette.textPrimary}")
    }
    rule(".mk-plan .mk-body") {
        property("text-wrap", "pretty")
    }
    rule(".mk-price-block") {
        column(Spacing.xxs)
    }
    rule(".mk-price") {
        tabular()
        property("font-size", Marketing.FONT_PRICE)
        property("font-weight", "${FontWeight.semibold}")
        property("line-height", "1")
        property("letter-spacing", "-0.04em")
        property("color", "${Palette.textPrimary}")
    }
    rule(".mk-price-cadence, .mk-price-note") {
        property("font-size", "${Typography.bodySmall.size}")
        property("color", "${Palette.textSecondary}")
        property("text-wrap", "pretty")
    }
    rule(".mk-feats") {
        property("margin", "0")
        property("padding", "0")
        property("list-style", "none")
        column(Spacing.sm)
        property("flex", "1 1 auto")
    }
    rule(".mk-feat") {
        property("display", "flex")
        property("align-items", "flex-start")
        property("gap", "${Spacing.sm}")
        property("font-size", "${Typography.body.size}")
        property("line-height", "1.45")
        property("color", "${Palette.textPrimary}")
        property("text-wrap", "pretty")
    }
    rule(".mk-feat--planned") {
        property("color", "${Palette.textSecondary}")
    }
    rule(".mk-feat-icon") {
        property("flex", "none")
        property("display", "inline-flex")
        property("margin-top", "0.2em")
        property("color", "${Palette.textPrimary}")
    }
    rule(".mk-feat--planned .mk-feat-icon") {
        property("color", "${Palette.textTertiary}")
    }
    rule(".mk-feat-icon .mk-ico") {
        property("width", "1rem")
        property("height", "1rem")
    }
    rule(".mk-callout") {
        property("display", "flex")
        property("flex-wrap", "wrap")
        property("align-items", "center")
        property("gap", "${Spacing.lg}")
        property("padding", "${Spacing.xl}")
        flatCard()
    }
    rule(".mk-callout-copy") {
        property("flex", "1 1 16rem")
        column(Spacing.xxs)
    }
}

private fun InitSilkContext.registerCalculator() {
    rule(".mk-calc-card") {
        property("width", "100%")
        property("max-width", Marketing.NARROW_MEASURE)
        property("margin", "0 auto")
        column(Spacing.md)
        property("padding", "clamp(${Spacing.md}, 4vw, ${Spacing.xl})")
        property("box-sizing", "border-box")
        flatCard()
    }
    rule(".mk-seat-head") {
        property("display", "flex")
        property("align-items", "baseline")
        property("justify-content", "space-between")
    }
    rule(".mk-seat-label") {
        property("font-size", "${Typography.bodySmall.size}")
        property("color", "${Palette.textSecondary}")
    }
    rule(".mk-seat-count") {
        tabular()
        property("font-size", Marketing.FONT_SEATS)
        property("font-weight", "${FontWeight.semibold}")
        property("letter-spacing", "-0.03em")
        property("line-height", "1")
        property("color", "${Palette.textPrimary}")
    }
    rule(".mk-slider") {
        property("width", "100%")
        property("height", "1.5rem")
        property("cursor", "pointer")
        property("accent-color", "${Palette.accent}")
    }
    rule(".mk-seat-ends") {
        tabular()
        property("display", "flex")
        property("justify-content", "space-between")
        property("font-size", "${Typography.caption.size}")
        property("color", "${Palette.textTertiary}")
    }
    rule(".mk-bars-v") {
        column(Spacing.md)
        property("margin-top", "${Spacing.sm}")
    }
    rule(".mk-pbar") {
        column(Spacing.xs)
    }
    rule(".mk-pbar-head") {
        property("display", "flex")
        property("flex-wrap", "wrap")
        property("justify-content", "space-between")
        property("gap", "${Spacing.xxs} ${Spacing.sm}")
        property("font-size", "${Typography.bodySmall.size}")
    }
    rule(".mk-pbar-label") {
        property("font-weight", "${FontWeight.medium}")
        property("color", "${Palette.textSecondary}")
    }
    rule(".mk-pbar--accent .mk-pbar-label") {
        property("font-weight", "${FontWeight.semibold}")
        property("color", "${Palette.textPrimary}")
    }
    rule(".mk-pbar-price") {
        tabular()
        property("color", "${Palette.textPrimary}")
        property("font-weight", "${FontWeight.semibold}")
    }
    rule(".mk-pbar-note") {
        property("font-size", "${Typography.caption.size}")
        property("font-weight", "${FontWeight.regular}")
        property("color", "${Palette.textTertiary}")
    }
    rule(".mk-pbar-track") {
        property("height", "0.625rem")
        property("overflow", "hidden")
        property("border-radius", "${Radius.pill}")
        property("background-color", "${Palette.background}")
    }
    rule(".mk-pbar-fill") {
        property("display", "block")
        property("height", "100%")
        property("border-radius", "${Radius.pill}")
        property("background-color", "color-mix(in srgb, ${Palette.textTertiary} 45%, ${Palette.background})")
        property("transform-origin", "left center")
        property("transform", "scaleX(calc(var(--w, 0) / 100))")
        property("transition", Motion.transitionWith(Marketing.DUR_LIFT, Motion.EASE_STANDARD, "transform"))
    }
    rule(".mk-pbar--accent .mk-pbar-fill") {
        property("background-color", "${Palette.textPrimary}")
    }
    rule(".mk-saving") {
        property("display", "flex")
        property("align-items", "flex-start")
        property("gap", "${Spacing.sm}")
        property("padding", "${Spacing.md}")
        property("border-radius", rowRadius)
        property("font-weight", "${FontWeight.medium}")
        property("line-height", "1.45")
        property("color", "${Palette.textPrimary}")
        property("background-color", "${Palette.background}")
        property("text-wrap", "pretty")
    }
    rule(".mk-saving .mk-ico") {
        property("margin-top", "0.15em")
        property("color", "${Palette.textPrimary}")
    }
    rule(".mk-foot") {
        column(Spacing.xs)
        property("font-size", "${Typography.caption.size}")
        property("color", "${Palette.textTertiary}")
    }
    rule(".mk-foot-links") {
        property("display", "flex")
        property("flex-wrap", "wrap")
        property("gap", "${Spacing.xs}")
    }
    rule(".mk-foot-link.mk-foot-link, .mk-foot-link.mk-foot-link:visited") {
        property("color", "${Palette.textTertiary}")
        property("text-decoration", "underline")
    }
    rule(".mk-foot-note") {
        property("margin", "0")
        property("font-size", "${Typography.bodySmall.size}")
        property("line-height", "1.5")
        property("color", "${Palette.textSecondary}")
        property("text-wrap", "pretty")
    }
}

private fun InitSilkContext.registerFold() {
    rule(".mk-faq") {
        column(Spacing.xs)
    }
    rule(".mk-fold") {
        property("border-radius", rowRadius)
        property("background-color", "${Palette.surfaceSubtle}")
        property("overflow", "hidden")
    }
    rule(".mk-fold-head") {
        property("appearance", "none")
        property("width", "100%")
        property("min-height", "3.5rem")
        property("display", "flex")
        property("align-items", "center")
        property("gap", "${Spacing.md}")
        property("padding", "${Spacing.md} ${Spacing.lg}")
        property("border", "0")
        property("background", "transparent")
        property("font-family", "inherit")
        property("font-size", "${Typography.body.size}")
        property("font-weight", "${FontWeight.medium}")
        property("line-height", "1.35")
        property("text-align", "left")
        property("color", "${Palette.textPrimary}")
        property("cursor", "pointer")
    }
    rule(".mk-fold-head:focus-visible") {
        property("outline", FocusRing.OUTLINE)
        property("outline-offset", "-${FocusRing.OFFSET}")
    }
    rule(".mk-fold-title") {
        property("flex", "1 1 auto")
        property("text-wrap", "pretty")
    }
    rule(".mk-fold-head > .mk-ico:last-child") {
        property("color", "${Palette.textSecondary}")
        property("transition", Motion.transitionWith(Marketing.DUR_LIFT, Motion.EASE_STANDARD, "transform"))
    }
    rule(".mk-fold--open .mk-fold-head > .mk-ico:last-child") {
        property("transform", "rotate(180deg)")
    }
    rule(".mk-fold-body") {
        property("padding", "0 ${Spacing.lg} ${Spacing.lg}")
    }
    rule(".mk-fold-body .mk-body") {
        property("text-wrap", "pretty")
    }
}

private fun InitSilkContext.registerBandSurfaces() {
    rule(".mk-band .mk-guide, .mk-band .mk-formcard, .mk-band .mk-calc-card, .mk-band .mk-fold, .mk-qcard .mk-fold") {
        property("background-color", "${Palette.surface}")
    }
    rule(".mk-band .mk-plan, .mk-band .mk-tl-card") {
        property("background-color", "${Palette.surface}")
    }
}

private fun InitSilkContext.registerTimeline() {
    rule(".mk-timeline") {
        property("position", "relative")
        property("margin", "0")
        property("padding", "0 0 0 $railInset")
        property("list-style", "none")
        column(Spacing.md)
    }
    rule(".mk-timeline::before") {
        property("content", "''")
        property("position", "absolute")
        property("top", "0")
        property("bottom", "0")
        property("left", "calc($railDot / 2 - 1px)")
        property("width", "2px")
        property("background-color", "${Palette.border}")
    }
    rule(".mk-tl-item") {
        property("position", "relative")
    }
    rule(".mk-tl-node") {
        property("position", "absolute")
        property("top", "${Spacing.lg}")
        property("left", "calc(-1 * $railInset)")
        property("width", railDot)
        property("height", railDot)
        property("border-radius", "50%")
        property("background-color", "${Palette.textTertiary}")
    }
    rule(".mk-tl-item--latest .mk-tl-node") {
        property("background-color", "${Palette.textPrimary}")
    }
    rule(".mk-tl-card") {
        column(Spacing.sm)
        property("padding", "${Spacing.lg}")
        flatCard()
    }
    rule(".mk-tl-meta") {
        property("display", "flex")
        property("flex-wrap", "wrap")
        property("align-items", "center")
        property("gap", "${Spacing.sm}")
    }
    rule(".mk-version") {
        tabular()
        property("padding", "${Spacing.xxs} ${Spacing.sm}")
        property("border-radius", "${Radius.pill}")
        property("font-size", "${Typography.caption.size}")
        property("font-weight", "${FontWeight.semibold}")
        property("color", "${Palette.onPrimary}")
        property("background-color", "${Palette.primary}")
    }
    rule(".mk-date") {
        tabular()
        property("font-size", "${Typography.bodySmall.size}")
        property("color", "${Palette.textSecondary}")
    }
    rule(".mk-tl-title") {
        property("margin", "0")
        property("font-weight", "${FontWeight.semibold}")
        property("font-size", Marketing.FONT_TRUST_LEAD)
        property("line-height", "1.15")
        property("letter-spacing", "-0.03em")
        property("color", "${Palette.textPrimary}")
        property("text-wrap", "balance")
    }
    rule(".mk-tags") {
        property("display", "flex")
        property("flex-wrap", "wrap")
        property("gap", "${Spacing.xs}")
    }
    rule(".mk-tag") {
        property("padding", "${Spacing.xxs} ${Spacing.sm}")
        property("border-radius", "${Radius.pill}")
        property("font-size", "${Typography.caption.size}")
        property("font-weight", "${FontWeight.medium}")
        property("color", "${Palette.textSecondary}")
        property("background-color", "${Palette.background}")
    }
    rule(".mk-tl-list") {
        property("margin", "0")
        property("padding", "0")
        property("list-style", "none")
        column(Spacing.xs)
    }
    rule(".mk-tl-list li") {
        property("display", "flex")
        property("align-items", "flex-start")
        property("gap", "${Spacing.xs}")
        property("line-height", "1.5")
        property("color", "${Palette.textSecondary}")
        property("text-wrap", "pretty")
    }
    rule(".mk-tl-list .mk-ico") {
        property("margin-top", "0.2em")
        property("color", "${Palette.textPrimary}")
    }
    rule(".mk-tl-end") {
        property("margin", "${Spacing.xl} 0 0")
        property("text-align", "center")
        property("font-size", "${Typography.bodySmall.size}")
        property("color", "${Palette.textTertiary}")
    }
}

private fun InitSilkContext.registerDocsAndContact() {
    rule(".mk-qsteps") {
        property("margin", "0")
        property("padding", "0")
        property("list-style", "none")
        column(Spacing.xl)
    }
    rule(".mk-qstep") {
        property("display", "grid")
        property("grid-template-columns", "$roundNumeral minmax(0, 1fr)")
        property("gap", "${Spacing.md}")
        property("align-items", "start")
    }
    rule(".mk-qnum") {
        tabular()
        property("display", "grid")
        property("place-items", "center")
        property("width", roundNumeral)
        property("height", roundNumeral)
        property("border-radius", "50%")
        property("font-weight", "${FontWeight.semibold}")
        property("color", "${Palette.textPrimary}")
        property("background-color", "${Palette.surfaceSubtle}")
    }
    rule(".mk-qcard") {
        column(Spacing.sm)
        property("min-width", "0")
        property("padding-top", "${Spacing.xxs}")
    }
    rule(".mk-qcopy") {
        column(Spacing.xxs)
    }
    rule(".mk-qcopy .mk-body") {
        property("text-wrap", "pretty")
    }

    rule(".mk-guides") {
        property("display", "grid")
        property("grid-template-columns", "repeat(3, minmax(0, 1fr))")
        property("gap", "${Spacing.md}")
    }
    mediaRule(".mk-guides", compact) {
        property("grid-template-columns", "minmax(0, 1fr)")
    }
    rule(".mk-guide") {
        column(Spacing.sm)
        property("align-items", "flex-start")
        property("min-width", "0")
        property("padding", "${Spacing.xl} ${Spacing.lg}")
        flatCard()
    }
    rule(".mk-guide .mk-body") {
        property("text-wrap", "pretty")
    }
    rule(".mk-guide > .mk-ico") {
        property("width", "1.5rem")
        property("height", "1.5rem")
        property("margin-bottom", "${Spacing.xs}")
        property("color", "${Palette.textPrimary}")
    }
    rule(".mk-sla") {
        property("margin-top", "auto")
        property("font-size", "${Typography.bodySmall.size}")
        property("color", "${Palette.textSecondary}")
    }
    rule(".mk-mail.mk-mail, .mk-mail.mk-mail:visited") {
        property("font-size", "${Typography.body.size}")
        property("font-weight", "${FontWeight.medium}")
        property("color", "${Palette.textPrimary}")
        property("text-decoration", "underline")
        property("text-underline-offset", "0.2em")
        property("overflow-wrap", "anywhere")
    }
    rule(".mk-formcard") {
        column(Spacing.sm)
        property("padding", "clamp(${Spacing.md}, 5vw, ${Spacing.xxl})")
        flatCard()
    }
    rule(".mk-fields") {
        column(Spacing.xs)
        property("margin", "${Spacing.sm} 0")
    }
    rule(".mk-field-label") {
        property("margin-top", "${Spacing.xs}")
        property("font-size", "${Typography.bodySmall.size}")
        property("font-weight", "${FontWeight.medium}")
        property("color", "${Palette.textPrimary}")
    }
    rule(".mk-textarea") {
        property("width", "100%")
        property("box-sizing", "border-box")
        property("min-height", "9rem")
        property("padding", "${Spacing.xs} ${Spacing.sm}")
        property("border-radius", "${Radius.control}")
        property("border", "1px solid ${Palette.border}")
        property("background-color", "${Palette.background}")
        property("color", "${Palette.textPrimary}")
        property("font-family", "inherit")
        property("font-size", "${Typography.body.size}")
        property("resize", "vertical")
    }
    rule(".mk-textarea:focus-visible") {
        property("outline", FocusRing.OUTLINE)
        property("outline-offset", FocusRing.OFFSET)
    }
}

private fun InitSilkContext.registerSignIn() {
    rule(".mk-signin") {
        property("display", "flex")
        property("justify-content", "center")
        property("width", "100%")
        property("box-sizing", "border-box")
        property("padding", "calc(${Marketing.HERO_TOP_PAD} * 0.75 + ${HomeTokens.NAV_CLEARANCE}) ${Marketing.GUTTER} ${Marketing.SECTION_PAD}")
    }
    rule(".mk-signin-stack") {
        column(Spacing.md)
        property("align-items", "center")
        property("width", "100%")
        property("max-width", "28rem")
    }
    rule(".mk-signin-card") {
        column(Spacing.md)
        property("align-items", "center")
        property("width", "100%")
        property("box-sizing", "border-box")
        property("padding", "${Spacing.xl}")
        property("text-align", "center")
        flatCard()
    }
    rule(".mk-signin-logo") {
        property("display", "block")
        property("width", "3rem")
        property("height", "3rem")
    }
    rule(".mk-signin-title") {
        property("margin", "0")
        property("font-size", "${Typography.h2.size}")
        property("font-weight", "${FontWeight.semibold}")
        property("line-height", "1.15")
        property("letter-spacing", "-0.03em")
        property("color", "${Palette.textPrimary}")
        property("text-wrap", "balance")
    }
    rule(".mk-signin-lede") {
        property("margin", "0 0 ${Spacing.xs}")
        property("font-size", "${Typography.body.size}")
        property("line-height", "1.5")
        property("color", "${Palette.textSecondary}")
        property("text-wrap", "balance")
    }
    rule(".mk-signin-error") {
        property("width", "100%")
        property("box-sizing", "border-box")
        property("padding", "${Spacing.sm} ${Spacing.md}")
        property("border-radius", rowRadius)
        property("background-color", "${Palette.background}")
        property("font-size", "${Typography.bodySmall.size}")
        property("line-height", "1.5")
        property("color", "${Palette.status(StatusHue.Danger).solid}")
        property("text-wrap", "pretty")
    }
    rule(".mk-signin-error--info") {
        property("color", "${Palette.textPrimary}")
    }
    rule(".mk-signin-fine") {
        property("margin", "${Spacing.xs} 0 0")
        property("font-size", "${Typography.caption.size}")
        property("line-height", "1.5")
        property("color", "${Palette.textTertiary}")
        property("text-wrap", "balance")
    }
    rule(".mk-signin-fine a, .mk-signin-fine a:visited") {
        property("color", "${Palette.textSecondary}")
        property("text-decoration", "underline")
        property("text-underline-offset", "0.2em")
    }
    rule(".mk-signin-alt.mk-signin-alt, .mk-signin-alt.mk-signin-alt:visited") {
        property("font-size", "${Typography.bodySmall.size}")
        property("font-weight", "${FontWeight.medium}")
        property("color", "${Palette.textSecondary}")
        property("text-decoration", "none")
        property("transition", Motion.transition(Motion.DUR_BASE, "color"))
    }
    mediaRule(".mk-signin-alt.mk-signin-alt:hover", HoverFinePointerMedia) {
        property("color", "${Palette.textPrimary}")
    }
}

private fun InitSilkContext.registerLegal() {
    rule(".mk-legal-hero") {
        property("width", "100%")
        property("max-width", "67.5rem")
        property("margin", "0 auto")
        property("box-sizing", "border-box")
        property("padding", "calc(${Marketing.HERO_TOP_PAD} * 1.1 + ${HomeTokens.NAV_CLEARANCE} * 0.5) ${Spacing.lg} ${Spacing.xl}")
    }
    rule(".mk-legal-title") {
        property("margin", "0")
        property("font-weight", "${FontWeight.semibold}")
        property("font-size", Marketing.FONT_H1)
        property("line-height", "1.04")
        property("letter-spacing", "-0.04em")
        property("color", "${Palette.textPrimary}")
        property("text-wrap", "balance")
    }
    rule(".mk-legal-meta") {
        column(Spacing.xxs)
        property("margin-top", "${Spacing.lg}")
    }
    rule(".mk-legal-meta-row") {
        property("display", "flex")
        property("flex-wrap", "wrap")
        property("gap", "${Spacing.xs}")
        property("font-size", "${Typography.body.size}")
    }
    rule(".mk-legal-meta-label") {
        property("min-width", "7.5rem")
        property("color", "${Palette.textSecondary}")
    }
    rule(".mk-legal-meta-value") {
        tabular()
        property("color", "${Palette.textPrimary}")
        property("font-weight", "${FontWeight.medium}")
    }
    rule(".mk-legal-summary") {
        property("margin-top", "${Spacing.xl}")
        property("padding", "${Spacing.lg}")
        column(Spacing.xs)
        flatCard()
    }
    rule(".mk-legal-summary-title") {
        property("margin", "0")
        property("font-size", "${Typography.body.size}")
        property("font-weight", "${FontWeight.semibold}")
        property("color", "${Palette.textPrimary}")
    }
    rule(".mk-legal-summary-body") {
        property("margin", "0")
        property("font-size", "${Typography.body.size}")
        property("line-height", "1.65")
        property("color", "${Palette.textPrimary}")
        property("text-wrap", "pretty")
    }
    rule(".mk-legal-section") {
        property("margin-bottom", "${Spacing.x3l}")
        property("scroll-margin-top", Marketing.ANCHOR_OFFSET)
        column(Spacing.xs)
    }
    rule(".mk-legal-num") {
        tabular()
        property("font-size", "${Typography.bodySmall.size}")
        property("font-weight", "${FontWeight.medium}")
        property("color", "${Palette.textTertiary}")
    }
    rule(".mk-legal-section-title") {
        property("margin", "0 0 ${Spacing.xs}")
        property("font-size", Marketing.FONT_TRUST_LEAD)
        property("font-weight", "${FontWeight.semibold}")
        property("line-height", "1.2")
        property("letter-spacing", "-0.03em")
        property("color", "${Palette.textPrimary}")
        property("text-wrap", "balance")
    }
    rule(".mk-legal-body") {
        property("margin", "0")
        property("font-size", "${Typography.body.size}")
        property("line-height", "1.75")
        property("color", "${Palette.textSecondary}")
        property("text-wrap", "pretty")
    }
    rule(".mk-legal-extra") {
        property("margin-top", "${Spacing.sm}")
    }
    rule(".mk-legal-bullets") {
        property("margin", "0")
        property("padding", "0")
        property("list-style", "none")
        column(Spacing.sm)
    }
    rule(".mk-legal-bullets li") {
        property("display", "flex")
        property("align-items", "flex-start")
        property("gap", "${Spacing.sm}")
        property("font-size", "${Typography.body.size}")
        property("line-height", "1.7")
        property("color", "${Palette.textSecondary}")
        property("text-wrap", "pretty")
    }
    rule(".mk-legal-bullets li::before") {
        property("content", "''")
        property("flex", "none")
        property("width", "0.3125rem")
        property("height", "0.3125rem")
        property("margin-top", "0.7em")
        property("border-radius", "50%")
        property("background-color", "${Palette.textTertiary}")
    }
    rule(".mk-legal-lead") {
        property("margin", "${Spacing.lg} 0 ${Spacing.xs}")
        property("font-size", "${Typography.body.size}")
        property("font-weight", "${FontWeight.semibold}")
        property("color", "${Palette.textPrimary}")
    }
    rule(".mk-legal-link.mk-legal-link, .mk-legal-link.mk-legal-link:visited") {
        property("font-weight", "${FontWeight.medium}")
        property("color", "${Palette.textPrimary}")
        property("text-decoration", "underline")
        property("text-underline-offset", "0.2em")
    }
    rule(".mk-site .sb-toc.mk-toc a") {
        padding()
        property("border-left", "0")
        property("border-radius", "${Radius.control}")
        property("font-size", "${Typography.bodySmall.size}")
        property("color", "${Palette.textSecondary}")
        property("text-decoration", "none")
    }
    mediaRule(".mk-site .sb-toc.mk-toc a:hover", HoverFinePointerMedia) {
        property("color", "${Palette.textPrimary}")
        property("background-color", "${Palette.surfaceSubtle}")
    }
    rule(".mk-toc-heading") {
        property("padding", "0 ${Spacing.sm}")
        property("margin-bottom", "${Spacing.xs}")
        property("font-size", "${Typography.bodySmall.size}")
        property("font-weight", "${FontWeight.semibold}")
        property("color", "${Palette.textPrimary}")
    }
    rule(".mk-toc-num") {
        tabular()
        property("margin-right", "${Spacing.xs}")
        property("color", "${Palette.textTertiary}")
    }
}

private fun InitSilkContext.registerPageReducedMotion() {
    reducedMotionRule(".mk-fold-head > .mk-ico:last-child") {
        property("transform", "none !important")
    }
    reducedMotionRule(".mk-fold--open .mk-fold-head > .mk-ico:last-child") {
        property("transform", "rotate(180deg) !important")
    }
}
